// ComponentContextMenu.js - Context menu for adding components to canvas

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { COMPONENT_LIBRARY, COMPONENT_CATEGORIES } from '../../data/componentLibrary';
import useStore from '../../store/useStore';
import THEME from '../../styles/theme';

const ComponentContextMenu = ({ position, onClose, onAddComponent }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set(['sources']));
  const searchInputRef = useRef(null);
  const { componentLibrary, catalogComponents } = useStore();

  // Auto-focus search input when menu opens
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Auto-expand categories with matches when searching
  useEffect(() => {
    if (searchTerm) {
      // Expand all categories that have matching components
      const categoriesToExpand = new Set();

      Object.entries(COMPONENT_CATEGORIES).forEach(([categoryKey, category]) => {
        // Check generic components
        const hasGenericMatch = category.components.some(compType => {
          const comp = COMPONENT_LIBRARY[compType];
          return comp && comp.displayName.toLowerCase().includes(searchTerm.toLowerCase());
        });

        // Check catalog components
        const hasCatalogMatch = catalogComponents.some(catalogComp =>
          catalogComp.category === categoryKey && (
            catalogComp.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            catalogComp.partNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            catalogComp.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        );

        if (hasGenericMatch || hasCatalogMatch) {
          categoriesToExpand.add(categoryKey);
        }
      });

      setExpandedCategories(categoriesToExpand);
    }
  }, [searchTerm, catalogComponents]);

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  const handleComponentClick = (componentType) => {
    onAddComponent(componentType);
    onClose();
  };

  // Filter and organize components
  const getFilteredComponents = () => {
    const filteredCategories = {};

    // Process each category
    Object.entries(COMPONENT_CATEGORIES).forEach(([categoryKey, category]) => {
      const components = [];

      // Add generic components from this category
      category.components.forEach(compType => {
        const comp = COMPONENT_LIBRARY[compType];
        if (comp) {
          const matchesSearch = searchTerm === '' ||
            comp.displayName.toLowerCase().includes(searchTerm.toLowerCase());

          if (matchesSearch) {
            components.push({
              type: compType,
              displayName: comp.displayName,
              isGeneric: true
            });
          }
        }
      });

      // Add catalog components from this category
      catalogComponents.forEach(catalogComp => {
        if (catalogComp.category === categoryKey) {
          const matchesSearch = searchTerm === '' ||
            catalogComp.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            catalogComp.partNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            catalogComp.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase());

          if (matchesSearch) {
            components.push({
              type: catalogComp.type,
              displayName: catalogComp.displayName,
              partNumber: catalogComp.partNumber,
              manufacturer: catalogComp.manufacturer,
              isCatalog: true,
              isFavorite: catalogComp.isFavorite
            });
          }
        }
      });

      if (components.length > 0) {
        filteredCategories[categoryKey] = {
          name: category.name,
          components
        };
      }
    });

    return filteredCategories;
  };

  const filteredCategories = getFilteredComponents();

  return (
    <>
      <Backdrop onClick={onClose} />
      <Menu style={{ top: position.y, left: position.x }}>
        <SearchBox>
          <SearchInput
            ref={searchInputRef}
            type="text"
            placeholder="Search components..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBox>

        <ComponentList>
          {Object.entries(filteredCategories).map(([categoryKey, category]) => (
            <CategorySection key={categoryKey}>
              <CategoryHeader
                onClick={() => toggleCategory(categoryKey)}
                isExpanded={expandedCategories.has(categoryKey)}
              >
                <Arrow isExpanded={expandedCategories.has(categoryKey)}>▶</Arrow>
                <CategoryName>{category.name}</CategoryName>
                <ComponentCount>{category.components.length}</ComponentCount>
              </CategoryHeader>

              {expandedCategories.has(categoryKey) && (
                <ComponentsContainer>
                  {category.components.map(comp => (
                    <ComponentItem
                      key={comp.type}
                      onClick={() => handleComponentClick(comp.type)}
                      isCatalog={comp.isCatalog}
                    >
                      {comp.isFavorite && <FavoriteStar>★</FavoriteStar>}
                      <ComponentName>{comp.displayName}</ComponentName>
                      {comp.partNumber && (
                        <PartNumber>{comp.partNumber}</PartNumber>
                      )}
                    </ComponentItem>
                  ))}
                </ComponentsContainer>
              )}
            </CategorySection>
          ))}

          {Object.keys(filteredCategories).length === 0 && (
            <EmptyMessage>No components found</EmptyMessage>
          )}
        </ComponentList>
      </Menu>
    </>
  );
};

// Styled Components
const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${THEME.zIndex.modal - 1};
`;

const Menu = styled.div`
  position: fixed;
  width: 300px;
  max-height: 500px;
  background: ${THEME.sidebarBackground};
  border: 1px solid ${THEME.borderColor};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: ${THEME.zIndex.modal};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 150ms ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const SearchBox = styled.div`
  padding: 12px;
  border-bottom: 1px solid ${THEME.borderColor};
  background: rgba(0, 0, 0, 0.2);
`;

const SearchInput = styled.input`
  width: 100%;
  background: ${THEME.inputBackground};
  color: ${THEME.textPrimary};
  border: 1px solid ${THEME.inputBorder};
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 13px;
  transition: all ${THEME.transitionFast};

  &:focus {
    outline: none;
    border-color: ${THEME.accent};
    box-shadow: 0 0 0 1px ${THEME.accent};
  }

  &::placeholder {
    color: ${THEME.textMuted};
  }
`;

const ComponentList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(220, 20, 60, 0.4);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(220, 20, 60, 0.6);
  }
`;

const CategorySection = styled.div`
  margin-bottom: 4px;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  gap: 8px;
  cursor: pointer;
  border-radius: 4px;
  border-left: 3px solid ${props => props.isExpanded ? THEME.accent : 'transparent'};
  background: ${props => props.isExpanded ? 'rgba(220, 38, 38, 0.08)' : 'transparent'};
  transition: all ${THEME.transitionFast};
  user-select: none;

  &:hover {
    background: ${props => props.isExpanded ? 'rgba(220, 38, 38, 0.12)' : 'rgba(255, 255, 255, 0.05)'};
  }
`;

const Arrow = styled.span`
  color: ${props => props.isExpanded ? THEME.accent : THEME.textSecondary};
  font-size: 10px;
  transition: transform ${THEME.transitionFast};
  transform: rotate(${props => props.isExpanded ? '90deg' : '0deg'});
`;

const CategoryName = styled.span`
  color: ${THEME.textPrimary};
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 1;
`;

const ComponentCount = styled.span`
  color: ${THEME.textMuted};
  font-size: 11px;
`;

const ComponentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 20px;
  margin-top: 4px;
`;

const ComponentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  background: ${THEME.cardBackground};
  border: 1px solid #000000;
  border-left: ${props => props.isCatalog ? '3px solid #2196F3' : '3px solid rgba(220, 38, 38, 0.3)'};
  transition: all ${THEME.transitionFast};
  position: relative;

  &:hover {
    background: ${THEME.panelBackground};
    border-color: ${THEME.accent};
    transform: translateX(4px);
  }

  &:active {
    transform: translateX(2px) scale(0.98);
  }
`;

const FavoriteStar = styled.span`
  position: absolute;
  top: 2px;
  right: 4px;
  color: #FFD700;
  font-size: 12px;
`;

const ComponentName = styled.span`
  color: ${THEME.textPrimary};
  font-size: 12px;
  font-weight: 500;
  flex: 1;
`;

const PartNumber = styled.span`
  color: #ff6b6b;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
`;

const EmptyMessage = styled.div`
  color: ${THEME.textMuted};
  font-size: 13px;
  text-align: center;
  padding: 32px 16px;
`;

export default ComponentContextMenu;
